/**
 * ECReceiptsTab — „EC-Belege erfassen mit KI" (Jun 2026, User-Wunsch).
 *
 * Zwei Wege zum selben Abgleich-/Buchungs-Flow:
 *  1) Belege scannen: Kassenschnitt (Z-Bon) + N Einzel-EC-Belege hochladen →
 *     Gemini-Vision liest Summen/Rechnungsnummern → Abgleich Tagessumme vs.
 *     Einzelbelege → Zuordnung zu offenen Rechnungen → buchen.
 *  2) Digital abrufen: EC-Terminal-Dienstleister (SumUp/Zettle/Concardis/CCV)
 *     verbinden und Transaktionen direkt abrufen (aktuell GEMOCKT).
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
    Upload, ScanLine, Check, AlertTriangle, Loader2, Plug, Trash2,
    RefreshCw, FileText, X, ToggleLeft, ToggleRight, CreditCard, Download, Wand2,
} from "lucide-react";
import api, { formatPrice } from "../../lib/api";
import { previewPdfResponse } from "../../lib/pdfPreview";
import { Button } from "../../components/ui/button";
import KiBuchungssatzModal from "./KiBuchungssatzModal";
import SumUpTerminalPanel from "./SumUpTerminalPanel";
import SumUpReconciliationPanel from "./SumUpReconciliationPanel";

const fmtEur = (v) => Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

export default function ECReceiptsTab({ mode: modeProp }) {
    // Jul-2026 (User-Wunsch): Modus wird über die Kopf-Buttons der Buchhaltung
    // gesteuert („Belege scannen (KI)" / „Digital vom Terminal" neben Mahnlauf).
    const [mode, setMode] = useState(modeProp || "scan"); // scan | digital
    const [kassenschnitt, setKassenschnitt] = useState(null); // {file_url, extracted}
    const [receipts, setReceipts] = useState([]); // [{file_url, extracted}]
    const [reconcile, setReconcile] = useState(null);
    const [selections, setSelections] = useState({}); // receipt_index -> invoice_id
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const [scanningKind, setScanningKind] = useState(null);
    const [autoBook, setAutoBook] = useState(false);
    const [providers, setProviders] = useState([]);
    const [source, setSource] = useState("scan");
    const [sessions, setSessions] = useState([]);
    const [kiBeleg, setKiBeleg] = useState(null); // EC-Beleg (extracted) für KI-Buchungssatz

    // Jun-2026 (User-Wunsch) — KI-Buchungssatz-Vorbefüllung aus einem EC-Beleg.
    const kiPrefillEc = (ex) => ({
        buchungstext: [ex?.merchant, ex?.invoice_number ? `Beleg ${ex.invoice_number}` : "", ex?.card_last4 ? `EC •${ex.card_last4}` : ""]
            .filter(Boolean).join(" · ") || "EC-Kartenzahlung",
        betrag_brutto: ex?.amount || 0,
        ust_satz: 19,
        richtung: "ausgabe",
        kontenrahmen: "SKR03",
        geschaeftspartner: ex?.merchant || null,
        zahlungsart: "bank",
        source_type: "ec-beleg",
        source_id: ex?.invoice_number || null,
        beleg_datum: ex?.date || null,
        card_last4: ex?.card_last4 || null,
    });

    const loadSettings = useCallback(async () => {
        try {
            const [s, p, sess] = await Promise.all([
                api.get("/buchhaltung/ec/settings"),
                api.get("/buchhaltung/ec/providers"),
                api.get("/buchhaltung/ec/sessions?limit=8"),
            ]);
            setAutoBook(!!s.data.auto_book);
            setProviders(p.data.items || []);
            setSessions(sess.data.items || []);
        } catch { /* ignore */ }
    }, []);

    useEffect(() => { loadSettings(); }, [loadSettings]);

    const openReport = async (sid) => {
        try {
            const r = await api.get(`/buchhaltung/ec/sessions/${sid}/report`, { responseType: "blob" });
            previewPdfResponse(r, `Kassenbericht_${sid.slice(0, 8)}.pdf`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kassenbericht konnte nicht erstellt werden");
        }
    };

    const resetFlow = () => {
        setKassenschnitt(null); setReceipts([]); setReconcile(null);
        setSelections({}); setResult(null); setSource("scan");
    };

    // Externe Modus-Umschaltung (Kopf-Buttons der Buchhaltung) übernehmen.
    useEffect(() => {
        if (modeProp && modeProp !== mode) {
            setMode(modeProp);
            resetFlow();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modeProp]);

    // ---- Scannen ----
    const scanFile = async (file, kind) => {
        if (!file) return;
        setScanningKind(kind);
        const form = new FormData();
        form.append("file", file);
        form.append("kind", kind);
        try {
            const r = await api.post("/buchhaltung/ec/scan", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            if (kind === "kassenschnitt") {
                setKassenschnitt(r.data);
                toast.success(`Kassenschnitt erkannt: ${fmtEur(r.data.extracted.ec_total)} · ${r.data.extracted.tx_count ?? "?"} Trx`);
            } else {
                setReceipts((rs) => [...rs, r.data]);
                toast.success(`Beleg erkannt: ${fmtEur(r.data.extracted.amount)}`);
            }
            setReconcile(null); setResult(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || "KI-Erkennung fehlgeschlagen");
        }
        setScanningKind(null);
    };

    const onPickReceipts = async (fileList) => {
        for (const f of Array.from(fileList || [])) {
            // sequenziell, damit die KI-Aufrufe nicht parallel rate-limiten
            await scanFile(f, "receipt");
        }
    };

    const removeReceipt = (idx) => setReceipts((rs) => rs.filter((_, i) => i !== idx));

    // ---- Digital ----
    const connect = async (key) => {
        try {
            const r = await api.post(`/buchhaltung/ec/providers/${key}/connect`);
            toast.success(r.data.mocked === false ? "SumUp live verbunden" : "Verbunden (Demo)");
            loadSettings();
        }
        catch (e) { toast.error(e.response?.data?.detail || "Verbinden fehlgeschlagen"); }
    };
    const disconnect = async (key) => {
        try { await api.post(`/buchhaltung/ec/providers/${key}/disconnect`); loadSettings(); }
        catch (e) { toast.error(e.response?.data?.detail || "Trennen fehlgeschlagen"); }
    };
    const syncProvider = async (key) => {
        setBusy(true);
        try {
            const r = await api.post(`/buchhaltung/ec/providers/${key}/sync`, { date: new Date().toISOString().slice(0, 10) });
            setKassenschnitt({ file_url: null, extracted: r.data.kassenschnitt });
            setReceipts((r.data.receipts || []).map((rc) => ({ file_url: null, extracted: rc })));
            setSource(`digital:${key}`);
            setReconcile(null); setResult(null);
            toast.success(`${r.data.provider_name}: ${r.data.receipts.length} Transaktionen abgerufen`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Abruf fehlgeschlagen");
        }
        setBusy(false);
    };

    // ---- Auto-Book-Setting ----
    const toggleAuto = async () => {
        const next = !autoBook;
        setAutoBook(next);
        try { await api.put("/buchhaltung/ec/settings", { auto_book: next }); }
        catch { setAutoBook(!next); toast.error("Einstellung konnte nicht gespeichert werden"); }
    };

    // ---- Reconcile ----
    const doReconcile = async () => {
        if (!kassenschnitt) { toast.error("Bitte zuerst einen Kassenschnitt erfassen"); return; }
        if (receipts.length === 0) { toast.error("Bitte mindestens einen EC-Beleg erfassen"); return; }
        setBusy(true);
        try {
            const r = await api.post("/buchhaltung/ec/reconcile", {
                kassenschnitt: kassenschnitt.extracted,
                receipts: receipts.map((x) => x.extracted),
            });
            setReconcile(r.data);
            const pre = {};
            r.data.matches.forEach((m) => {
                if (m.candidates.length > 0 && m.candidates[0].score >= 60) pre[m.tx_index] = m.candidates[0].invoice_id;
            });
            setSelections(pre);
            setResult(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Abgleich fehlgeschlagen");
        }
        setBusy(false);
    };

    // ---- Commit ----
    const doCommit = async () => {
        const matches = reconcile.matches
            .filter((m) => selections[m.tx_index])
            .map((m) => {
                const inv = m.candidates.find((c) => c.invoice_id === selections[m.tx_index]);
                return {
                    invoice_id: selections[m.tx_index],
                    amount: Math.min(m.tx.amount, inv?.remaining || m.tx.amount),
                    invoice_number: inv?.invoice_number,
                    file_url: receipts[m.tx_index]?.file_url,
                    card_last4: receipts[m.tx_index]?.extracted?.card_last4,
                };
            });
        if (matches.length === 0) { toast.error("Keine Buchung ausgewählt"); return; }
        if (!window.confirm(`${matches.length} EC-Zahlung(en) als bezahlt buchen?`)) return;
        setBusy(true);
        try {
            const r = await api.post("/buchhaltung/ec/commit", {
                source,
                date: new Date().toISOString().slice(0, 10),
                kassenschnitt: kassenschnitt.extracted,
                receipts: receipts.map((x) => x.extracted),
                reconciliation: reconcile.reconciliation,
                matches,
            });
            setResult(r.data);
            toast.success(`${r.data.count_booked} Zahlung(en) gebucht`);
            loadSettings();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Buchung fehlgeschlagen");
        }
        setBusy(false);
    };

    const rec = reconcile?.reconciliation;
    const selCount = Object.keys(selections).filter((k) => selections[k]).length;

    return (
        <div data-testid="ec-receipts-tab" className="space-y-5">
            {/* Auto-Book (Modus-Umschalter wanderte in die Buchhaltungs-Kopfzeile, Jul-2026) */}
            <div className="flex items-center justify-end flex-wrap gap-3">
                <button type="button" onClick={toggleAuto} className="inline-flex items-center gap-2 text-sm font-bold text-gray-700" data-testid="ec-auto-book-toggle">
                    {autoBook ? <ToggleRight className="w-6 h-6 text-emerald-600" /> : <ToggleLeft className="w-6 h-6 text-gray-400" />}
                    Auto-Buchung {autoBook ? "an" : "aus"}
                </button>
            </div>

            {/* Digital: Provider-Karten */}
            {mode === "digital" && (
                <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="ec-providers-block">
                    <div className="flex items-center gap-2 mb-3">
                        <CreditCard className="w-4 h-4" />
                        <h3 className="font-bold text-base">EC-Terminal-Dienstleister</h3>
                        {providers.some((p) => p.mocked === false) ? (
                            <span className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-sm font-bold" data-testid="ec-live-badge">SumUp live · andere Demo</span>
                        ) : (
                            <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-0.5 rounded-sm font-bold">Demo / gemockt</span>
                        )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        {providers.map((p) => (
                            <div key={p.key} className="border border-gray-200 rounded-sm p-3" data-testid={`ec-provider-${p.key}`}>
                                <div className="font-bold text-sm flex items-center gap-1.5">{p.name}
                                    {p.mocked === false && <span className="text-[9px] uppercase bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-sm font-black" data-testid={`ec-live-${p.key}`}>Live</span>}
                                </div>
                                <div className="text-[11px] text-gray-500 mb-2 min-h-[28px]">{p.hint}</div>
                                {p.connected ? (
                                    <div className="space-y-1.5">
                                        <Button type="button" onClick={() => syncProvider(p.key)} disabled={busy}
                                            className="w-full h-8 rounded-sm bg-[#FFCC00] text-[#111827] hover:bg-[#E6B800] text-xs font-black" data-testid={`ec-sync-${p.key}`}>
                                            {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />} Abrufen
                                        </Button>
                                        <button type="button" onClick={() => disconnect(p.key)} className="w-full text-[11px] text-gray-500 hover:text-red-600 inline-flex items-center justify-center gap-1" data-testid={`ec-disconnect-${p.key}`}>
                                            <Trash2 className="w-3 h-3" /> Trennen
                                        </button>
                                    </div>
                                ) : (
                                    <Button type="button" onClick={() => connect(p.key)} variant="outline" className="w-full h-8 rounded-sm text-xs font-bold" data-testid={`ec-connect-${p.key}`}>
                                        <Plug className="w-3 h-3 mr-1" /> Verbinden
                                    </Button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* SumUp Solo-Terminal (LIVE): Kopplung + Zahlung auslösen */}
            {mode === "digital" && providers.some((p) => p.key === "sumup" && p.connected && p.mocked === false) && (
                <>
                    <SumUpTerminalPanel />
                    {/* Jul-2026 (User-Wunsch) — Auto-„Bezahlt"-Abgleich Übersicht */}
                    <SumUpReconciliationPanel />
                </>
            )}

            {/* Scan: Uploads */}
            {mode === "scan" && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Kassenschnitt */}
                    <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="ec-kassenschnitt-block">
                        <h3 className="font-bold text-sm mb-2 flex items-center gap-1.5"><FileText className="w-4 h-4" /> 1. Kassenschnitt / Z-Bon</h3>
                        {!kassenschnitt ? (
                            <label className="block border-2 border-dashed border-gray-300 rounded-sm p-5 text-center cursor-pointer hover:border-[#FFCC00]" data-testid="ec-kassenschnitt-drop">
                                <input type="file" accept="image/*,application/pdf" className="hidden"
                                    onChange={(e) => scanFile(e.target.files?.[0], "kassenschnitt")} data-testid="ec-kassenschnitt-input" />
                                {scanningKind === "kassenschnitt"
                                    ? <span className="text-sm text-gray-500 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> KI liest Beleg…</span>
                                    : <span className="text-sm text-gray-500 inline-flex items-center gap-2"><Upload className="w-4 h-4" /> Foto/PDF des Tagesabschlusses</span>}
                            </label>
                        ) : (
                            <div className="bg-gray-50 border border-gray-200 rounded-sm p-3 text-sm" data-testid="ec-kassenschnitt-result">
                                <div className="flex items-center justify-between">
                                    <span className="font-bold">EC-Tagessumme</span>
                                    <span className="font-mono-num font-black text-lg">{fmtEur(kassenschnitt.extracted.ec_total)}</span>
                                </div>
                                <div className="text-[11px] text-gray-500 mt-1">{kassenschnitt.extracted.tx_count ?? "?"} Transaktionen · Terminal {kassenschnitt.extracted.terminal_id || "—"} · {kassenschnitt.extracted.date || ""}</div>
                                <button type="button" onClick={() => setKassenschnitt(null)} className="text-[11px] text-red-600 mt-1 inline-flex items-center gap-1" data-testid="ec-kassenschnitt-remove"><X className="w-3 h-3" /> Entfernen</button>
                            </div>
                        )}
                    </div>

                    {/* Einzelbelege */}
                    <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="ec-receipts-block">
                        <h3 className="font-bold text-sm mb-2 flex items-center gap-1.5"><CreditCard className="w-4 h-4" /> 2. Einzel-EC-Belege ({receipts.length})</h3>
                        <label className="block border-2 border-dashed border-gray-300 rounded-sm p-3 text-center cursor-pointer hover:border-[#FFCC00] mb-2" data-testid="ec-receipts-drop">
                            <input type="file" accept="image/*,application/pdf" multiple className="hidden"
                                onChange={(e) => onPickReceipts(e.target.files)} data-testid="ec-receipts-input" />
                            {scanningKind === "receipt"
                                ? <span className="text-sm text-gray-500 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> KI liest Beleg…</span>
                                : <span className="text-sm text-gray-500 inline-flex items-center gap-2"><Upload className="w-4 h-4" /> Belege hinzufügen (mehrere möglich)</span>}
                        </label>
                        <ul className="divide-y divide-gray-100 max-h-44 overflow-auto">
                            {receipts.map((rc, i) => (
                                <li key={i} className="py-1.5 flex items-center justify-between text-sm" data-testid={`ec-receipt-row-${i}`}>
                                    <span className="text-gray-600 text-xs">{rc.extracted.invoice_number || rc.extracted.merchant || `Beleg ${i + 1}`} {rc.extracted.card_last4 ? `· •${rc.extracted.card_last4}` : ""}</span>
                                    <span className="inline-flex items-center gap-2">
                                        <span className="font-mono-num font-bold">{fmtEur(rc.extracted.amount)}</span>
                                        <button type="button" onClick={() => setKiBeleg(rc.extracted)} className="text-[#111827] hover:text-black" title="KI-Buchungssatz vorschlagen" data-testid={`ec-receipt-ki-${i}`}><Wand2 className="w-3.5 h-3.5" /></button>
                                        <button type="button" onClick={() => removeReceipt(i)} className="text-gray-400 hover:text-red-600" data-testid={`ec-receipt-remove-${i}`}><X className="w-3.5 h-3.5" /></button>
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            {/* Abgleich-Button */}
            {kassenschnitt && receipts.length > 0 && !result && (
                <div className="flex justify-end">
                    <Button type="button" onClick={doReconcile} disabled={busy}
                        className="rounded-sm bg-[#111827] text-[#FFCC00] hover:bg-black font-bold uppercase tracking-wider" data-testid="ec-reconcile-btn">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <ScanLine className="w-4 h-4 mr-1.5" />} Abgleichen & Rechnungen zuordnen
                    </Button>
                </div>
            )}

            {/* Reconcile-Ergebnis + Matching */}
            {rec && !result && (
                <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="ec-reconcile-block">
                    <div className={`px-5 py-3 flex items-center justify-between flex-wrap gap-2 ${rec.balanced ? "bg-emerald-50 border-b border-emerald-200" : "bg-amber-50 border-b border-amber-200"}`} data-testid="ec-reconcile-summary">
                        <div className="flex items-center gap-2">
                            {rec.balanced ? <Check className="w-5 h-5 text-emerald-700" /> : <AlertTriangle className="w-5 h-5 text-amber-700" />}
                            <div className="text-sm">
                                <span className="font-bold">{rec.balanced ? "Abgleich stimmt" : "Abweichung erkannt"}</span>
                                <span className="ml-2 text-gray-600">Belege {fmtEur(rec.receipts_sum)} ({rec.receipts_count}) vs. Kassenschnitt {fmtEur(rec.ec_total)} ({rec.tx_count_declared ?? "?"})</span>
                                {Math.abs(rec.diff) >= 0.01 && <span className="ml-2 font-bold text-amber-700" data-testid="ec-reconcile-diff">Differenz {fmtEur(rec.diff)}</span>}
                            </div>
                        </div>
                        <Button type="button" onClick={doCommit} disabled={busy}
                            className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black rounded-sm uppercase tracking-wider" data-testid="ec-commit-btn">
                            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />} Als bezahlt buchen ({selCount})
                        </Button>
                    </div>
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                            <tr>
                                <th className="text-left px-3 py-2">Beleg</th>
                                <th className="text-right px-3 py-2">Betrag</th>
                                <th className="text-left px-3 py-2">Offene Rechnung zuordnen</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reconcile.matches.map((m) => (
                                <tr key={m.tx_index} className="border-t border-gray-100" data-testid={`ec-match-${m.tx_index}`}>
                                    <td className="px-3 py-2 text-xs text-gray-600">{m.tx.purpose || `Beleg ${m.tx_index + 1}`}</td>
                                    <td className="px-3 py-2 text-right font-mono-num font-bold">{fmtEur(m.tx.amount)}</td>
                                    <td className="px-3 py-2 text-xs">
                                        {m.candidates.length === 0 ? (
                                            <span className="text-gray-400 italic">Keine offene Rechnung gefunden</span>
                                        ) : (
                                            <select value={selections[m.tx_index] || ""}
                                                onChange={(e) => setSelections((s) => ({ ...s, [m.tx_index]: e.target.value }))}
                                                className="border border-gray-300 rounded-sm px-2 py-1 text-xs bg-white min-w-[280px]"
                                                data-testid={`ec-match-pick-${m.tx_index}`}>
                                                <option value="">— nicht buchen —</option>
                                                {m.candidates.map((c) => (
                                                    <option key={c.invoice_id} value={c.invoice_id}>
                                                        {c.invoice_number || "(Entwurf)"} · {c.customer_label} · {fmtEur(c.remaining)} offen ({c.score}%)
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Buchungs-Ergebnis */}
            {result && (
                <div className="bg-white border border-green-200 rounded-sm p-5" data-testid="ec-result-block">
                    <div className="flex items-center gap-2 mb-3"><Check className="w-5 h-5 text-green-700" /><h3 className="font-bold text-base">Buchung abgeschlossen</h3></div>
                    <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="bg-green-50 border border-green-200 rounded-sm p-3 text-center">
                            <div className="text-[10px] uppercase tracking-wider text-gray-600">Gebucht</div>
                            <div className="text-2xl font-black font-mono-num" data-testid="ec-result-booked">{result.count_booked}</div>
                        </div>
                        <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-center">
                            <div className="text-[10px] uppercase tracking-wider text-gray-600">Fehler</div>
                            <div className="text-2xl font-black font-mono-num">{result.count_errors}</div>
                        </div>
                    </div>
                    {result.booked.length > 0 && (
                        <ul className="border border-gray-200 rounded-sm divide-y">
                            {result.booked.map((b) => (
                                <li key={b.ec_payment_id} className="px-3 py-2 text-xs flex items-center justify-between">
                                    <span><span className="font-bold">{b.invoice_number}</span><span className="ml-2 text-gray-500">→ Status {b.new_status}</span></span>
                                    <span className="font-mono-num font-bold text-green-700">{fmtEur(b.amount)}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    <div className="mt-4 flex justify-end gap-2">
                        {result.session_id && (
                            <Button type="button" onClick={() => openReport(result.session_id)}
                                className="rounded-sm bg-[#111827] text-[#FFCC00] hover:bg-black font-bold" data-testid="ec-result-report-btn">
                                <FileText className="w-4 h-4 mr-1.5" /> Tages-Kassenbericht (PDF)
                            </Button>
                        )}
                        <Button type="button" onClick={resetFlow} variant="outline" className="rounded-sm" data-testid="ec-reset-btn">Nächster Kassenschnitt</Button>
                    </div>
                </div>
            )}

            {/* Kassenbericht-Historie (Steuerberater-/DATEV-Übergabe) */}
            {sessions.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="ec-sessions-block">
                    <h3 className="font-bold text-sm mb-3 flex items-center gap-1.5"><Download className="w-4 h-4" /> Letzte Tages-Kassenberichte</h3>
                    <ul className="divide-y divide-gray-100">
                        {sessions.map((s) => (
                            <li key={s.id} className="py-2 flex items-center justify-between text-sm" data-testid={`ec-session-${s.id}`}>
                                <div className="min-w-0">
                                    <span className="font-bold">{s.date}</span>
                                    <span className="ml-2 text-gray-500 text-xs">
                                        {s.source?.startsWith("digital") ? s.source.replace("digital:", "Digital · ") : "Beleg-Scan"} · {s.booked_count || 0} gebucht · {fmtEur((s.kassenschnitt || {}).ec_total)}
                                        {(s.reconciliation || {}).balanced ? "" : " · ⚠ Abweichung"}
                                    </span>
                                </div>
                                <Button type="button" onClick={() => openReport(s.id)} variant="outline" className="h-8 rounded-sm text-xs font-bold shrink-0" data-testid={`ec-session-report-${s.id}`}>
                                    <FileText className="w-3.5 h-3.5 mr-1" /> PDF
                                </Button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {kiBeleg && <KiBuchungssatzModal prefill={kiPrefillEc(kiBeleg)} onClose={() => setKiBeleg(null)} />}
        </div>
    );
}
